import { watch } from 'vue'
import type { RouteLocationNormalized, Router } from 'vue-router'

import { screenOf } from './screens'

import { type ArrivalScene, useArrivalStore } from '@/stores/arrival'
import { useAuthStore } from '@/stores/auth'
import { useBrandStore } from '@/stores/brand'
import { useSetupStore } from '@/stores/setup'
import { useSurfaceStore } from '@/stores/surface'
import type { AuthClaims } from '@/types/auth'
import { isBuiltOnboardingChainStep, isOnboardingChainStep } from '@/types/onboarding-chain'

declare module 'vue-router' {
  interface RouteMeta {
    /** Route requires a signed-in user. */
    requiresAuth?: boolean
    /** Route requires the signed-in user's claims to carry a workspaceId. */
    requiresWorkspace?: boolean
    /** Route requires claims.entitled === true. */
    requiresEntitlement?: boolean
    /** Route is only reachable while signed out (redirects signed-in users away). */
    guestOnly?: boolean
  }
}

const AUTH_PATH = '/auth'
const BILLING_PATH = '/billing'
const SETUP_PATH = '/setup'
const ENTITLEMENT_REFRESH_THROTTLE_MS = 30000

let lastEntitlementRefreshAt = 0
let lastEntitlementRefreshUid: string | null = null

function isAuthPath(path: string): boolean {
  return path === AUTH_PATH || path.startsWith(`${AUTH_PATH}/`)
}

function isBillingPath(path: string): boolean {
  return path === BILLING_PATH || path.startsWith(`${BILLING_PATH}/`)
}

function isSetupPath(path: string): boolean {
  return path === SETUP_PATH || path.startsWith(`${SETUP_PATH}/`)
}

/** Holds the brand and setup reads while it waits for both to come back; the shell takes over the hold, and the linger covers the hand-off. */
async function resolvedSetup(): Promise<ReturnType<typeof useSetupStore>> {
  const setup = useSetupStore()
  const releases = [useBrandStore().retain(), setup.retain()]
  try {
    if (setup.resolved) return setup
    await new Promise<void>((resolve) => {
      const stop = watch(
        () => setup.resolved,
        (ready) => {
          if (!ready) return
          stop()
          resolve()
        },
      )
    })
    return setup
  } finally {
    for (const release of releases) release()
  }
}

/** Where a navigation has to go instead, while the brand still has setup to walk, or `null` when it may proceed. */
function setupRedirect(
  setup: ReturnType<typeof useSetupStore>,
  to: RouteLocationNormalized,
): string | null {
  const onSetupPath = isSetupPath(to.path)

  if (!setup.needsSetup) return onSetupPath ? '/' : null
  if (!onSetupPath) return `${SETUP_PATH}/${setup.currentStep}`

  const step = to.params.step
  const known = isOnboardingChainStep(step) && isBuiltOnboardingChainStep(step)
  return known ? null : `${SETUP_PATH}/${setup.currentStep}`
}

/** Where a user lands after signing in, given their claims. */
export function resolvePostSignInRoute(claims: AuthClaims | null): string {
  if (!claims?.workspaceId) return '/auth/full-name'
  if (!claims.entitled) return BILLING_PATH
  return '/'
}

/** Sends the user to the sign-in screen the moment the session ends, wherever they happen to be. */
function installSignOutRedirect(router: Router): void {
  const auth = useAuthStore()

  watch(
    () => auth.status,
    (status) => {
      if (status !== 'signed-out') return
      if (!router.currentRoute.value.meta.requiresAuth) return
      void router.replace({ path: AUTH_PATH })
    },
  )
}

/** True for a navigation that leaves a bare screen (sign-in, billing) for setup, which plays the arrival. */
function isArrivalNavigation(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  return to.name === 'setup' && from.meta.layout === 'bare'
}

/** True for a navigation that leaves a setup finished in this session for Home, which plays the handover. */
function isHandoverNavigation(to: RouteLocationNormalized, from: RouteLocationNormalized): boolean {
  return to.name === 'home' && from.name === 'setup' && useSetupStore().completedInSession
}

function sceneFor(to: RouteLocationNormalized, from: RouteLocationNormalized): ArrivalScene | null {
  if (isArrivalNavigation(to, from)) return 'arrival'
  if (isHandoverNavigation(to, from)) return 'handover'
  return null
}

/** Arms the arrival or the handover once its navigation has passed every guard, and disarms it if that navigation then fails. */
function installArrivalTrigger(router: Router): void {
  const arrival = useArrivalStore()

  router.beforeResolve((to, from) => {
    const scene = sceneFor(to, from)
    if (scene !== null) arrival.arm(scene)
  })

  router.afterEach((to, from, failure) => {
    if (failure && sceneFor(to, from) !== null) arrival.disarm()
  })
}

/** Installs the global auth/workspace/entitlement guards. Call once, at app boot. */
export function installGuards(router: Router): void {
  installSignOutRedirect(router)
  installArrivalTrigger(router)

  router.beforeEach(async (to) => {
    const auth = useAuthStore()

    if (auth.status === 'unknown') {
      await auth.init()
    }

    const { requiresAuth, requiresWorkspace, requiresEntitlement, guestOnly } = to.meta

    const onAuthPath = isAuthPath(to.path)
    const onBillingPath = isBillingPath(to.path)

    if (guestOnly && auth.isSignedIn) {
      return '/'
    }

    if (requiresAuth && !auth.isSignedIn && !onAuthPath) {
      return { path: AUTH_PATH, query: { redirect: to.fullPath } }
    }

    if (requiresWorkspace && !auth.hasWorkspace && !onAuthPath) {
      return '/auth/workspace-setup'
    }

    if (requiresEntitlement && !auth.isEntitled && !onBillingPath) {
      const uid = auth.user?.uid ?? null
      if (
        uid !== lastEntitlementRefreshUid ||
        Date.now() - lastEntitlementRefreshAt >= ENTITLEMENT_REFRESH_THROTTLE_MS
      ) {
        lastEntitlementRefreshUid = uid
        lastEntitlementRefreshAt = Date.now()
        try {
          await auth.refreshClaims()
        } catch {
          // auth.refreshClaims records its own error.
        }
      }
      if (!auth.isEntitled) return BILLING_PATH
    }

    if (to.meta.layout === 'shell' && auth.isSignedIn && auth.hasWorkspace && auth.isEntitled) {
      const redirect = setupRedirect(await resolvedSetup(), to)
      if (redirect && redirect !== to.path) return redirect
    }

    useSurfaceStore().enter(screenOf(to), to.path)

    return true
  })
}
